import { useState, useCallback, useEffect, useRef, type MutableRefObject } from "react";
import { analytics } from "../../lib/analytics";

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

interface UseVisitorFeedbackParams {
  feedbackEnabled: boolean;
  hasReachedThreshold: boolean;
  isListViewOpen: boolean;
  consentStatus: "pending" | "accepted" | "declined";
  poisViewedRef: MutableRefObject<number>;
  gpsActiveMinutes: number;
  orgSlug: string;
  mapSlug: string;
}

export interface UseVisitorFeedbackReturn {
  showBanner: boolean;
  feedbackPhase: "banner" | "form" | "thanks" | null;
  rating: number | null;
  comment: string;
  setRating: (n: number) => void;
  setComment: (s: string) => void;
  submit: () => void;
  skip: () => void;
  dismiss: () => void;
}

export function useVisitorFeedback({
  feedbackEnabled,
  hasReachedThreshold,
  isListViewOpen,
  consentStatus,
  poisViewedRef,
  gpsActiveMinutes,
  orgSlug,
  mapSlug,
}: UseVisitorFeedbackParams): UseVisitorFeedbackReturn {
  const [feedbackPhase, setFeedbackPhase] = useState<"banner" | "form" | "thanks" | null>(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackDismissed, setFeedbackDismissed] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const thanksTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stash phase when list view hides the banner, restore on close
  const stashedPhaseRef = useRef<"banner" | "form" | null>(null);

  // Trigger conditions (excluding list view — handled separately)
  const baseConditions =
    feedbackEnabled &&
    hasReachedThreshold &&
    !feedbackSubmitted &&
    !feedbackDismissed &&
    consentStatus === "accepted";

  const shouldShow = baseConditions && !isListViewOpen;

  // Auto-show banner when conditions met
  useEffect(() => {
    if (shouldShow && feedbackPhase === null && !stashedPhaseRef.current) {
      setFeedbackPhase("banner");
    }
  }, [shouldShow, feedbackPhase]);

  // Stash/restore phase when list view toggles
  useEffect(() => {
    if (isListViewOpen && (feedbackPhase === "banner" || feedbackPhase === "form")) {
      stashedPhaseRef.current = feedbackPhase;
      setFeedbackPhase(null);
    } else if (!isListViewOpen && stashedPhaseRef.current && baseConditions) {
      setFeedbackPhase(stashedPhaseRef.current);
      stashedPhaseRef.current = null;
    }
  }, [isListViewOpen, baseConditions]); // intentionally exclude feedbackPhase to avoid loop

  const submit = useCallback(() => {
    if (rating === null) return;

    const deviceType = getDeviceType();

    // PostHog event (funnel analytics)
    analytics.visitorFeedback({
      rating,
      comment: comment.trim() || undefined,
      gps_session_minutes: gpsActiveMinutes,
      pois_viewed: poisViewedRef.current,
      device_type: deviceType,
    });

    // DB write — fire-and-forget, non-blocking
    const apiBase = import.meta.env.VITE_API_URL || "";
    fetch(`${apiBase}/api/maps/visitor/${orgSlug}/${mapSlug}/feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        rating,
        comment: comment.trim() || undefined,
        feedbackType: "gps_exit_survey",
        deviceType,
        gpsSessionMinutes: gpsActiveMinutes,
        poisViewed: poisViewedRef.current,
      }),
    }).catch(() => {
      // Silent fail — PostHog event is the backup
    });

    setFeedbackSubmitted(true);
    setFeedbackPhase("thanks");

    thanksTimerRef.current = setTimeout(() => {
      setFeedbackPhase(null);
    }, 3000);
  }, [rating, comment, gpsActiveMinutes, poisViewedRef, orgSlug, mapSlug]);

  const skip = useCallback(() => {
    setFeedbackDismissed(true);
    setFeedbackPhase(null);
  }, []);

  const dismiss = useCallback(() => {
    setFeedbackDismissed(true);
    setFeedbackPhase(null);
  }, []);

  const handleSetRating = useCallback((n: number) => {
    setRating(n);
    setFeedbackPhase("form");
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (thanksTimerRef.current) clearTimeout(thanksTimerRef.current);
    };
  }, []);

  return {
    showBanner: shouldShow && feedbackPhase !== null,
    feedbackPhase,
    rating,
    comment,
    setRating: handleSetRating,
    setComment,
    submit,
    skip,
    dismiss,
  };
}
